import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { categories, featuredComics, latestUpdates } from '@/mocks/comics';
import SEOHead from '@/components/feature/SEOHead';

interface ComicItem {
  id: number;
  title: string;
  category: string;
  author: string;
  cover: string;
  rating: number;
  chapters: number;
  isNew?: boolean;
  isHot?: boolean;
  updateDate?: string;
  views?: number;
}

function CategoryPage() {
  const { categoryName } = useParams<{ categoryName: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [categoryName]);

  const currentCategory = categories.find((c) => c.id === categoryName);
  const allComics: ComicItem[] = [...featuredComics, ...latestUpdates];
  const filteredComics = categoryName && categoryName !== 'all'
    ? allComics.filter((c) => c.category === categoryName)
    : allComics;

  const categoryTitle = currentCategory?.name || '全部漫画';
  const categoryDesc = currentCategory?.description || '浏览全部韩国漫画作品';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://xxiut.cn',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryTitle,
        item: `https://xxiut.cn/category/${categoryName}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${categoryTitle}_高清韩国漫画_韩漫之家`}
        description={`韩漫之家${categoryTitle}分类 - ${categoryDesc}。高清韩国漫画免费在线阅读，精选优质韩漫资源。`}
        keywords={`${categoryTitle},韩国漫画,韩漫,高清漫画,韩漫之家`}
        canonicalUrl={`https://xxiut.cn/category/${categoryName}`}
        jsonLd={breadcrumbJsonLd}
      />

      <div className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-line mr-0.5"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 font-medium">
              {categoryTitle}
            </span>
          </nav>

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {categoryTitle}
            </h1>
            <p className="text-sm text-foreground-600">{categoryDesc}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            <Link
              to="/category/all"
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                !categoryName || categoryName === 'all'
                  ? 'bg-primary-500 text-background-50 font-medium'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                  categoryName === cat.id
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-600 text-sm">该分类暂无漫画，请浏览其他分类</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredComics.map((comic) => (
                <Link
                  key={`${comic.category}-${comic.id}`}
                  to={`/article/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-100">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${comic.title} - 韩漫之家高清韩国漫画`}
                    />
                    <div className="absolute top-2.5 left-2.5 flex flex-col gap-1">
                      {comic.isHot && (
                        <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded-md font-medium">
                          热门
                        </span>
                      )}
                      {comic.isNew && (
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded-md font-medium">
                          新作
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent pt-8 pb-3 px-3">
                      <div className="flex items-center gap-1 text-background-50 text-xs">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        <span>{comic.rating}</span>
                        <span className="ml-auto">{comic.chapters}话</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500">{comic.author}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CategoryPage;